import React, { useState } from "react";
import {
  Sparkles,
  Ticket,
  Trees,
  BedDouble,
  Star,
  Users,
  ArrowRight,
  Info,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Newsletter from "../components/Newsletter";
import { useToast } from "../hooks/use-toast";
import { earnEnjoy } from "../mock";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { Sparkles, Ticket, Trees, BedDouble, Star, Users };

const Icon = ({ name, ...props }) => {
  const C = ICONS[name] || Star;
  return <C {...props} />;
};

const EarnEnjoy = () => {
  const { toast } = useToast();
  const { heroImage, heroEyebrow, heroTitle, heroIntro, benefits, disclaimer } =
    earnEnjoy;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    amount: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Please add your name and email so we can reply." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/investment-interest`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        amount: form.amount,
        message: form.message,
      });
      toast({
        title: "Thank you",
        description: `Thanks ${form.name.split(" ")[0]} — a member of the team will be in touch with you.`,
      });
      setForm({ name: "", email: "", phone: "", amount: "", message: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-[#d9c3a8] px-4 py-3 rounded-sm outline-none focus:ring-2 focus:ring-[#ebc754] text-[#1e231f]";

  return (
    <div className="bg-[#f2e5de] min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[78vh] min-h-[540px] md:min-h-[460px] w-full overflow-hidden">
        <img src={heroImage} alt="Become a FERAL partner" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 pt-20 md:pt-0">
          <p className="text-[#ebc754] tracking-[0.22em] text-sm font-semibold mb-5">{heroEyebrow}</p>
          <h1 className="feral-serif text-[#ebc754] text-3xl md:text-4xl lg:text-5xl max-w-2xl leading-[1.15]">{heroTitle}</h1>
          <p className="text-[#f2e5de]/90 text-base md:text-lg mt-6 max-w-xl mx-auto leading-relaxed">{heroIntro}</p>
          <a href="#register" className="inline-flex items-center gap-2 mt-9 bg-[#ebc754] hover:bg-[#f2e5de] text-[#9b3d0a] px-9 py-3.5 tracking-[0.15em] text-sm font-semibold uppercase transition-colors duration-300">
            Register your interest <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* Vision */}
      <section className="feral-canvas py-16 md:py-24 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="overflow-hidden rounded-md shadow-xl">
            <img src="/vision.jpeg" alt="The FERAL vision for our sites" className="w-full aspect-[4/3] object-cover" />
          </div>
          <div className="text-left">
            <p className="text-[#9b3d0a] tracking-[0.22em] text-sm font-semibold mb-4">THE VISION</p>
            <h2 className="feral-serif text-[#9b3d0a] text-3xl md:text-4xl mb-5">Help us fulfil the vision</h2>
            <p className="text-[#1e231f] text-base md:text-lg leading-relaxed">
              We have a wild vision for our already beautiful sites across North Wales. Invest to help us bring it to life — and enjoy the spaces, stays and facilities you help create.
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto mt-14 md:mt-20">
          <div className="max-w-3xl">
            <p className="feral-serif text-[#9b3d0a] text-2xl md:text-3xl leading-snug mb-5">
              Natural wild pools. Outdoor rustic sauna cabins. Magical wildflower gardens.
            </p>
            <p className="text-[#1e231f] text-base md:text-lg leading-relaxed">
              Working with nature, not against it. FERAL signature facilities across all our sites.
            </p>
            <p className="text-[#6e5646] text-sm md:text-base leading-relaxed mt-4 tracking-wide">
              Regenerative design, wellbeing at the core — built for connection, timeless and sustainable.
            </p>
          </div>

          <div className="overflow-hidden rounded-md shadow-lg mt-10">
            <img
              src="/vision-sauna-cabins.jpg"
              alt="Rustic sauna and garden room cabins glowing amongst wildflower gardens at dusk"
              className="w-full aspect-[16/9] object-cover transition-transform duration-500 hover:scale-105"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 md:gap-5 mt-4 md:mt-5">
            {[
              { src: "/vision-wildpool.jpg", alt: "Natural wild swimming pool in a walled garden" },
              { src: "/vision-barn-pool.jpg", alt: "Rustic barn opening onto a wild pool and gardens" },
              { src: "/vision-garden-yoga.jpg", alt: "Yoga in the wildflower and kitchen gardens" },
              { src: "/vision-manor-market.jpg", alt: "FERAL estate lit up for an evening gathering" },
            ].map((img) => (
              <div key={img.src} className="overflow-hidden rounded-md shadow-lg">
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full aspect-[4/3] object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="feral-canvas py-20 md:py-28 px-6">
        <div className="max-w-2xl mx-auto text-center mb-14">
          <p className="text-[#9b3d0a] tracking-[0.22em] text-sm font-semibold mb-5">WHAT YOU'LL ENJOY</p>
          <h2 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl mb-5">The kinds of things members enjoy</h2>
          <p className="text-[#1e231f] text-base md:text-lg leading-relaxed">
            Become part of the movement and share in everything FERAL is building across North Wales. Your investment is returned along with redeemable rewards and interest, and bespoke offerings and rewards are tailored to your investment amount. Earn more than a typical bank savings account while helping build the future of FERAL.
          </p>
        </div>
        <div className="max-w-4xl mx-auto space-y-4">
          {benefits.map((b) => (
            <div
              key={b.text}
              className="flex items-center gap-5 bg-white border border-[#e4d6c2] rounded-md p-5 md:p-6 shadow-sm"
            >
              <div className="w-14 h-14 shrink-0 rounded-full bg-[#f0dbbe] text-[#9b3d0a] flex items-center justify-center">
                <Icon name={b.icon} size={26} />
              </div>
              <p className="text-[#2e322c] text-lg md:text-xl leading-snug">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Register */}
      <section id="register" className="feral-texture bg-[#e2c7b0] py-20 md:py-28 px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[#9b3d0a] tracking-[0.22em] text-sm font-semibold mb-4">GET INVOLVED</p>
            <h2 className="feral-serif text-[#9b3d0a] text-4xl md:text-5xl mb-4">Register your interest</h2>
            <p className="text-[#1e231f] text-base md:text-lg leading-relaxed">
              Leave your details below and a member of the team will be in touch with you.
            </p>
          </div>

          <form onSubmit={submit} className="bg-[#f3e7d6] border border-[#d9c3a8] rounded-md p-6 md:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1e231f] mb-1.5">Name *</label>
                <input className={inputCls} value={form.name} onChange={(e) => update("name", e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1e231f] mb-1.5">Email *</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1e231f] mb-1.5">Phone</label>
                <input className={inputCls} value={form.phone} onChange={(e) => update("phone", e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1e231f] mb-1.5">Amount of potential investment</label>
                <input className={inputCls} placeholder="£25,000" value={form.amount} onChange={(e) => update("amount", e.target.value)} />
                <p className="text-xs text-[#6e5646] mt-1.5">Minimum investment amount £25,000</p>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-[#1e231f] mb-1.5">Anything you'd like us to know?</label>
              <textarea rows={4} className={inputCls} value={form.message} onChange={(e) => update("message", e.target.value)} />
            </div>
            <button type="submit" disabled={submitting} data-testid="investment-interest-submit-button" className="w-full mt-6 bg-[#9b3d0a] hover:bg-[#7e3208] disabled:opacity-60 text-[#f2e5de] py-3.5 rounded-full tracking-[0.15em] text-sm font-semibold uppercase transition-colors duration-300">
              {submitting ? "Sending…" : "Register interest"}
            </button>
            <p className="text-center text-[#6e5646] text-sm mt-4">
              A member of the team will be in touch with you.
            </p>
          </form>

          <div className="flex items-start gap-3 bg-[#f0dbbe] border border-[#d9c3a8] rounded-md p-4 mt-8">
            <Info size={18} className="text-[#9b3d0a] mt-0.5 shrink-0" />
            <p className="text-[#7e3208] text-xs leading-relaxed">{disclaimer}</p>
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default EarnEnjoy;
